import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from '@shared/auth/AuthContext';

interface DeveloperModeContextType {
  developerMode: boolean;
  setDeveloperMode: (active: boolean) => void;
  toggleDeveloperMode: () => void;
  isDeveloper: boolean;
}

const LOCAL_STORAGE_KEY_PRIMARY = 'developer_mode';
const LOCAL_STORAGE_KEY_SECONDARY = 'wm_developer_mode';
const LOCAL_STORAGE_KEY_MODE = 'wm_manager_mode';

const DeveloperModeContext = createContext<DeveloperModeContextType>({
  developerMode: false,
  setDeveloperMode: () => {},
  toggleDeveloperMode: () => {},
  isDeveloper: false,
});

export const DeveloperModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, profile, adminProfile, role } = useAuth();

  // Evaluate if the current authenticated user has Developer privileges
  const isDeveloper = useMemo(() => {
    // Strictly require boolean flag from database (usuarios_admin.desenvolvedor)
    return adminProfile?.desenvolvedor === true || (profile as any)?.desenvolvedor === true;
  }, [profile, adminProfile]);

  const [developerModeState, setDeveloperModeState] = useState<boolean>(() => {
    try {
      const modeVal = localStorage.getItem(LOCAL_STORAGE_KEY_MODE);
      if (modeVal === 'developer') return true;
      if (modeVal === 'normal') return false;

      const storedPrimary = localStorage.getItem(LOCAL_STORAGE_KEY_PRIMARY);
      if (storedPrimary !== null) return storedPrimary === 'true';
      const storedSecondary = localStorage.getItem(LOCAL_STORAGE_KEY_SECONDARY);
      return storedSecondary === 'true';
    } catch {
      return false;
    }
  });

  // RBAC Enforcement: Active mode is forced to false if user lacks isDeveloper permission
  const activeMode = isDeveloper ? developerModeState : false;

  const setDeveloperMode = (active: boolean) => {
    if (!isDeveloper && active) {
      console.warn('[DeveloperMode] Security restriction: User lacks developer privileges.');
      return;
    }
    setDeveloperModeState(active);
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY_MODE, active ? 'developer' : 'normal');
      localStorage.setItem(LOCAL_STORAGE_KEY_PRIMARY, String(active));
      localStorage.setItem(LOCAL_STORAGE_KEY_SECONDARY, String(active));
    } catch (e) {
      console.error('[DeveloperMode] Failed to save developer mode in localStorage:', e);
    }
  };

  const toggleDeveloperMode = () => {
    setDeveloperMode(!activeMode);
  };

  useEffect(() => {
    if (!isDeveloper && developerModeState) {
      setDeveloperModeState(false);
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY_MODE, 'normal');
        localStorage.removeItem(LOCAL_STORAGE_KEY_PRIMARY);
        localStorage.removeItem(LOCAL_STORAGE_KEY_SECONDARY);
      } catch {}
    }
  }, [isDeveloper, developerModeState]);

  return (
    <DeveloperModeContext.Provider
      value={{
        developerMode: activeMode,
        setDeveloperMode,
        toggleDeveloperMode,
        isDeveloper,
      }}
    >
      {children}
    </DeveloperModeContext.Provider>
  );
};

export const useDeveloperMode = () => useContext(DeveloperModeContext);
